"use client";

import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import IosShareIcon from "@mui/icons-material/IosShare";
import Image from "next/image";

const STORAGE_KEY = "pwa-install-dismissed";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // No mostrar si ya está instalada o si el usuario ya la descartó
    if (isInStandaloneMode()) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const _ios = isIOS();
    setIos(_ios);

    if (_ios) {
      // iOS: mostrar instrucciones manuales después de 3s
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    // Android/Chrome: esperar el evento del navegador
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") dismiss();
    else dismiss();
  };

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  if (!show) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: { xs: 70, sm: 24 }, // 70px en móvil para no tapar nav bar
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1400,
        width: { xs: "calc(100% - 32px)", sm: 420 },
        borderRadius: 3,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        bgcolor: "background.paper",
        borderLeft: "4px solid",
        borderColor: "primary.main",
      }}
    >
      {/* Ícono de la app */}
      <Box sx={{ flexShrink: 0, width: 48, height: 48, position: "relative", borderRadius: 2, overflow: "hidden" }}>
        <Image src="/icons/icon-96x96.png" alt="D2G" fill sizes="48px" style={{ objectFit: "cover" }} />
      </Box>

      {/* Texto */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          Instala D2G
        </Typography>
        {ios ? (
          <Typography variant="caption" color="text.secondary">
            Toca <IosShareIcon sx={{ fontSize: 13, verticalAlign: "middle", mx: 0.25 }} /> y luego{" "}
            <strong>«Agregar a inicio»</strong>
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Accede más rápido desde tu pantalla de inicio
          </Typography>
        )}
      </Box>

      {/* Botón instalar (solo Android/Chrome) */}
      {!ios && (
        <Button
          variant="contained"
          size="small"
          startIcon={<GetAppIcon />}
          onClick={handleInstall}
          sx={{ flexShrink: 0, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Instalar
        </Button>
      )}

      {/* Cerrar */}
      <IconButton size="small" onClick={dismiss} sx={{ flexShrink: 0 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
